import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { useDeals, useEntityTitle, useRefs } from '../api/hooks'
import { подсказкаПустойДаты } from '../api/istochnik'
import type { ListParams } from '../api/types'
import { EntityPicker } from '../components/EntityPicker'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import type { EntityRef } from '../lib/entities'
import { fmtDate, fmtMoney, fmtText } from '../lib/format'
import { stageFilter } from '../lib/refs'

const PAGE = 50   // ядро отдаёт страницами по 50 (PAGE в core_api)

export default function Deals() {
  const [sp, setSp] = useSearchParams()
  // Компанию читаем из адреса, как в дебиторке: сюда ведут ссылки «все сделки клиента»
  // из карточки компании и с экрана 360. Без этого фильтра переход молча открывал бы
  // общий список всех сделок — пользователь не отличил бы его от отфильтрованного.
  const companyId = sp.get('company') || ''

  const [title, setTitle] = useState('')
  const [stage, setStage] = useState('')
  const [category, setCategory] = useState('')
  const [onlyOpen, setOnlyOpen] = useState(false)
  const [start, setStart] = useState(0)

  const refs = useRefs()
  const stageOptions = useMemo(() => refs.stageOptions(category), [refs, category])

  // Название выбранной компании: один запрос на компанию (кэш на 10 минут), а не на строку.
  const companyTitle = useEntityTitle('company', companyId || undefined)
  const companyValue: EntityRef | null = companyId
    ? { kind: 'company', id: companyId, title: companyTitle.data || `#${companyId}` }
    : null

  // Фильтр в формате Bitrix REST: ядро понимает %FIELD (LIKE) и обычное равенство.
  const params = useMemo<ListParams>(() => {
    const filter: Record<string, unknown> = {}
    if (companyId) filter['COMPANY_ID'] = companyId
    if (title.trim()) filter['%TITLE'] = title.trim()
    // при выбранной воронке берём STAGE_ID прямо из справочника; без справочника — собираем сами
    const exact = category ? stageOptions.find((o) => o.code === stage)?.id : ''
    Object.assign(filter, exact ? { STAGE_ID: exact } : stageFilter(stage, category))
    if (category) filter['CATEGORY_ID'] = category
    if (onlyOpen) filter['CLOSED'] = 'N'
    return { filter, order: { DATE_MODIFY: 'DESC' }, start }
  }, [companyId, title, stage, category, onlyOpen, start, stageOptions])

  const q = useDeals(params)
  const data = q.data

  // смена фильтра — всегда с первой страницы
  const resetAnd = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  // у другой воронки другой набор стадий — выбранная могла бы «повиснуть» и не найти ничего
  const changeCategory = (v: string) => {
    setStart(0)
    setCategory(v)
    if (v && stage && !refs.stageOptions(v).some((o) => o.code === stage)) setStage('')
  }

  const changeCompany = (v: EntityRef | null) => {
    setStart(0)
    // компания живёт в адресе, чтобы ссылку на «сделки клиента» можно было переслать;
    // копию делаем намеренно — правка sp на месте не считается изменением состояния
    const next = new URLSearchParams(sp)
    if (v) next.set('company', v.id)
    else next.delete('company')
    setSp(next, { replace: true })
  }

  return (
    <>
      <div className="page-head">
        <h1>Сделки</h1>
        {/* «в вашей зоне» рядом с числом, а не только в оговорке ниже: счётчик читают
            отдельно от текста, и «найдено: 1» без этой приписки — часть выдана за целое. */}
        <span className="muted">
          {data && !data.stale
            ? `найдено: ${data.total}${data.scope?.limited ? ' (в вашей зоне)' : ''}`
            : ''}
        </span>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="deals">
          <div style={{ flex: 2, minWidth: 220 }}>
            <EntityPicker
              kind="company" id="deal-company" label="Компания"
              value={companyValue} onChange={changeCompany}
            />
          </div>

          <div className="f" style={{ flex: 2, minWidth: 220 }}>
            <label htmlFor="t">Название содержит</label>
            <input id="t" value={title} onChange={(e) => resetAnd(setTitle)(e.target.value)} placeholder="например: буксир" />
          </div>

          <div className="f">
            <label htmlFor="s">Стадия</label>
            <select id="s" value={stage} onChange={(e) => resetAnd(setStage)(e.target.value)}
                    disabled={stageOptions.length === 0}>
              <option value="">любая</option>
              {stageOptions.map((o) => (
                <option key={o.code} value={o.code}>{o.name}</option>
              ))}
            </select>
          </div>

          <div className="f">
            <label htmlFor="c">Воронка</label>
            <select id="c" value={category} onChange={(e) => changeCategory(e.target.value)}
                    disabled={refs.categories.length === 0}>
              <option value="">любая</option>
              {refs.categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 130 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
              <input
                type="checkbox"
                checked={onlyOpen}
                onChange={(e) => resetAnd(setOnlyOpen)(e.target.checked)}
                style={{ width: 'auto' }}
              />
              только открытые
            </label>
          </div>
        </FilterBar>

        {/* без справочника фильтры по стадии/воронке выбирать не из чего — объясняем, почему */}
        {!refs.loaded && (
          <div className="hint" style={{ marginTop: 10 }}>
            Справочник ядра недоступен: стадии и ответственные показаны сырыми ID Bitrix,
            фильтры по стадии и воронке отключены.
          </div>
        )}
        {refs.loaded && refs.dictsStale && (
          <div className="hint" style={{ marginTop: 10 }}>
            Воронки и стадии — из старого кеша ядра (Bitrix не ответил). Если воронку недавно
            меняли, названия могут отставать.
          </div>
        )}
      </div>

      <QueryState isLoading={q.isLoading} error={q.error} stale={data?.stale} ageMin={data?.ageMin}
                  scope={data?.scope}>
        <div className="card">
          {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">ID</th>
                  <th>Название</th>
                  <th className="nowrap">Стадия</th>
                  <th className="nowrap">Воронка</th>
                  <th className="nowrap">Ответственный</th>
                  <th className="num">Сумма</th>
                  <th className="nowrap">Изменена</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items ?? []).map((d) => (
                  <tr key={d.ID}>
                    <td className="mono nowrap" data-label="ID">
                      <Link to={`/deals/${d.ID}`}>{d.ID}</Link>
                    </td>
                    <td data-label="Название">
                      <Link to={`/deals/${d.ID}`}>{fmtText(d.TITLE)}</Link>
                    </td>
                    <td className="nowrap" data-label="Стадия">
                      <span className={`badge ${refs.stageKind(d.STAGE_ID)}`}>{refs.stageName(d.STAGE_ID)}</span>
                    </td>
                    <td className="nowrap muted" data-label="Воронка">{refs.categoryName(d.CATEGORY_ID)}</td>
                    <td className="nowrap" data-label="Ответственный">{refs.userName(d.ASSIGNED_BY_ID)}</td>
                    <td className="num" data-label="Сумма">{fmtMoney(d.OPPORTUNITY, d.CURRENCY_ID || 'RUB')}</td>
                    {/* В своей базе времени правки Bitrix нет: прочерк с подсказкой,
                        а не «Invalid Date» (см. api/istochnik.ts). */}
                    <td className="nowrap muted" data-label="Изменена"
                        title={подсказкаПустойДаты(d.DATE_MODIFY)}>{fmtDate(d.DATE_MODIFY, true)}</td>
                  </tr>
                ))}
                {data && !data.stale && data.items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty">
                      {/* «в вашей зоне таких сделок нет» и «таких сделок нет» — разные
                          утверждения, и путать их нельзя: во втором человек ищет дальше,
                          в первом — идёт к тому, у кого зона шире. */}
                      <div className="empty-title">
                        {data.scope?.limited
                          ? 'В вашей зоне таких сделок нет — выборка сужена правами, а не фильтрами'
                          : 'Ничего не найдено под эти фильтры'}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {data && data.total > PAGE && (
            <div className="pager">
              <button disabled={start === 0 || q.isFetching} onClick={() => setStart(Math.max(0, start - PAGE))}>
                ← назад
              </button>
              <span className="muted">
                {start + 1}–{Math.min(start + PAGE, data.total)} из {data.total}
              </span>
              <button disabled={data.next === undefined || q.isFetching} onClick={() => setStart(data.next!)}>
                вперёд →
              </button>
            </div>
          )}
        </div>
      </QueryState>
    </>
  )
}
